import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Calendar, Clock, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useBlogSettings, resolveBlogLang } from "@/context/BlogContext";
import { SEOHead } from "@/components/SEOHead";
import { CategoryChip } from "@/components/blog/CategoryChip";
import { ShareButtons } from "@/components/blog/ShareButtons";
import { TableOfContents } from "@/components/blog/TableOfContents";
import { ReadingProgress } from "@/components/blog/ReadingProgress";
import { PostLanguageSwitcher } from "@/components/blog/PostLanguageSwitcher";
import { BlogCard } from "@/components/blog/BlogCard";
import {
  blogFetch,
  blogIncrementView,
  blogListPath,
  blogAbsoluteUrl,
  blogPostPath,
  formatBlogDate,
  readingTimeLabel,
  formatViews,
} from "@/lib/blog";

export function BlogDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang: uiLang, t, isRTL } = useLanguage();
  const settings = useBlogSettings();
  const lang = resolveBlogLang(uiLang, settings);

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    setPost(null);

    blogFetch(`/posts/${encodeURIComponent(slug)}`, { lang, signal: ctrl.signal })
      .then((data) => {
        setPost(data);
        setLoading(false);
        // Fire-and-forget view increment
        blogIncrementView(data.id);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        if (err.status === 404) {
          setError("404");
        } else {
          setError(err.message);
        }
        setLoading(false);
      });

    return () => ctrl.abort();
  }, [slug, lang]);

  // 404 → liste sayfasına gönder
  useEffect(() => {
    if (error === "404") {
      const t = setTimeout(() => navigate(blogListPath(lang), { replace: true }), 2500);
      return () => clearTimeout(t);
    }
  }, [error, lang, navigate]);

  if (loading) {
    return (
      <div className="pt-28 pb-16 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="animate-pulse space-y-6">
          <div className="h-6 bg-slate-200 rounded w-1/3" />
          <div className="h-12 bg-slate-200 rounded w-3/4" />
          <div className="h-4 bg-slate-200 rounded w-1/2" />
          <div className="h-72 bg-slate-200 rounded-2xl mt-8" />
          <div className="space-y-3 mt-8">
            <div className="h-4 bg-slate-100 rounded" />
            <div className="h-4 bg-slate-100 rounded w-11/12" />
            <div className="h-4 bg-slate-100 rounded w-10/12" />
          </div>
        </div>
      </div>
    );
  }

  if (error === "404") {
    return (
      <div className="pt-28 pb-16 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-6xl font-black text-slate-200 mb-4">404</p>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">{t("blog.notFound")}</h1>
        <p className="text-slate-500 mb-6">{t("blog.notFoundDesc")}</p>
        <Link
          to={blogListPath(lang)}
          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-full bg-[#22A7D1] hover:bg-[#1B8FB5] transition-all"
        >
          {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          {t("blog.backToBlog")}
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-28 pb-16 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-red-700">
        {t("blog.loadError")}
      </div>
    );
  }

  if (!post) return null;

  const url = blogAbsoluteUrl(blogPostPath(lang, post.slug));
  const fallbackImg = "/images/og-image.png";

  return (
    <>
      <SEOHead page="blog-detail" article={post} lang={lang} />
      <ReadingProgress />

      {/* HERO + Meta */}
      <section className="pt-28 pb-8 sm:pt-32 sm:pb-10 bg-gradient-to-b from-sky-50/50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="mb-6 flex items-center gap-2 text-xs text-slate-500" aria-label="Breadcrumb">
            <Link to={blogListPath(lang)} className="hover:text-[#22A7D1]">
              {t("nav.blog")}
            </Link>
            {post.category_name && (
              <>
                <span>/</span>
                <Link
                  to={`${blogListPath(lang)}?category=${post.category_slug}`}
                  className="hover:text-[#22A7D1]"
                >
                  {post.category_name}
                </Link>
              </>
            )}
          </nav>

          {post.category_name && (
            <div className="mb-4">
              <CategoryChip
                name={post.category_name}
                slug={post.category_slug}
                color={post.category_color}
                lang={lang}
                size="md"
                as="span"
              />
            </div>
          )}

          <h1 className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-black text-slate-900 leading-[1.1]">
            {post.title}
          </h1>

          {post.excerpt && (
            <p className="mt-5 text-lg text-slate-500 leading-relaxed">{post.excerpt}</p>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {formatBlogDate(post.published_at, lang)}
            </span>
            {post.reading_time && (
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {readingTimeLabel(post.reading_time, lang)}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <Eye className="w-4 h-4" />
              {formatViews(post.views, lang)}
            </span>
          </div>

          {/* Dil switcher (multilang açıkken) */}
          {settings.multilang_enabled && (
            <div className="mt-5">
              <PostLanguageSwitcher translations={post.translations || []} currentLang={lang} />
            </div>
          )}
        </div>
      </section>

      {/* Featured image */}
      {post.featured_image && (
        <section className="bg-white pb-2">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <img
              src={post.featured_image}
              alt={post.title}
              className="w-full h-auto max-h-[520px] object-cover rounded-2xl border border-slate-200"
              onError={(e) => (e.currentTarget.src = fallbackImg)}
            />
          </div>
        </section>
      )}

      {/* Article + sticky share + TOC */}
      <section className="py-10 sm:py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Sticky share — desktop sol */}
            <aside className="hidden lg:block lg:col-span-1">
              <div className="sticky top-28">
                <ShareButtons url={url} title={post.title} variant="sticky" />
              </div>
            </aside>

            {/* Article */}
            <div className="lg:col-span-8">
              <article
                id="blog-article"
                className="prose prose-slate prose-lg max-w-none prose-headings:tracking-tight prose-headings:font-bold prose-h2:text-2xl prose-h3:text-xl prose-a:text-[#22A7D1] hover:prose-a:text-[#1B8FB5] prose-img:rounded-xl prose-img:border prose-img:border-slate-200 prose-blockquote:border-l-[#22A7D1] prose-blockquote:bg-sky-50/40 prose-blockquote:py-1 prose-blockquote:not-italic prose-pre:bg-slate-900 prose-pre:rounded-xl"
                dangerouslySetInnerHTML={{ __html: post.content || "" }}
                dir={isRTL ? "rtl" : "ltr"}
              />

              {/* Etiketler */}
              {post.tags && post.tags.length > 0 && (
                <div className="mt-10 pt-6 border-t border-slate-200 flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`${blogListPath(lang)}/etiket/${encodeURIComponent(tag)}`}
                      className="text-xs px-3 py-1.5 rounded-lg bg-slate-50 text-slate-600 hover:bg-sky-50 hover:text-[#22A7D1] transition-colors"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              )}

              {/* Mobile share */}
              <div className="lg:hidden mt-8 pt-6 border-t border-slate-200">
                <ShareButtons url={url} title={post.title} variant="row" />
              </div>
            </div>

            {/* TOC — desktop sağ (DESKTOP-ONLY karar) */}
            <aside className="hidden lg:block lg:col-span-3">
              <TableOfContents />
            </aside>
          </div>
        </div>
      </section>

      {/* İlgili Yazılar */}
      {post.related && post.related.length > 0 && (
        <section className="py-14 sm:py-20 bg-slate-50/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-8">
              {t("blog.related")}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {post.related.map((p) => (
                <BlogCard key={p.id} post={p} lang={lang} />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
